// The journal balance frame (wiki/pr/JOURNAL-REALTIME-PUSH-20261102.md, Go
// events.JettonBalanceEvent): one (master, owner) row of our journal after a
// settlement, on `holders:<master>` (event `holders.journal`) and on
// `jettons:<owner>` (event `jettons.balance`). `version` is the only order
// key — never lt or ts.

import { canonicalTonAddress } from './tonAddress'
import { addressToRawForm } from './tonRawAddress'

export const HOLDERS_JOURNAL_EVENT = 'holders.journal'
export const JETTONS_BALANCE_EVENT = 'jettons.balance'

/** A journal row as the frame states it. */
export type JournalBalance = {
  /** the jetton master's key (raw form when parseable) */
  master: string
  /** the owner's key (raw form when parseable) */
  owner: string
  /** the owner's address as sent (EQ form) */
  ownerAddress: string
  /** the row's version: the order key */
  version: bigint
  /** the balance in raw units; null = unknown (the journal saw the owner
      spend more than it saw arrive: net < 0) */
  amount: bigint | null
}

const keyOf = (addr: string) => addressToRawForm(addr) ?? addr

function bigintOf(v: unknown, signed: boolean): bigint | null {
  if (typeof v === 'number' && Number.isSafeInteger(v)) return BigInt(v)
  if (typeof v === 'string' && (signed ? /^-?\d+$/ : /^\d+$/).test(v)) return BigInt(v)
  return null
}

/** The frame's row; null for anything that is not a well-formed journal frame. */
export function parseJournalBalance(data: unknown): JournalBalance | null {
  if (data == null || typeof data !== 'object') return null
  const d = data as Record<string, unknown>
  if (typeof d.master !== 'string' || d.master === '') return null
  if (typeof d.owner !== 'string' || d.owner === '') return null
  const version = bigintOf(d.version, false)
  const amount = bigintOf(d.amount_raw, false)
  const net = bigintOf(d.net_raw, true)
  if (version == null || amount == null || net == null) return null
  return {
    master: keyOf(d.master),
    owner: keyOf(d.owner),
    ownerAddress: d.owner,
    version,
    amount: net < 0n ? null : amount,
  }
}

/** The holders channel of a token (the gateway names it by the EQ form). */
export function holdersChannel(master: string | null | undefined): `holders:${string}` | null {
  const eq = canonicalTonAddress(master ?? null)
  return eq ? `holders:${eq}` : null
}

/** The journal-balances channel of an owner (EQ form). */
export function jettonsChannel(owner: string | null | undefined): `jettons:${string}` | null {
  const eq = canonicalTonAddress(owner ?? null)
  return eq ? `jettons:${eq}` : null
}

/** Whole tokens from raw units, exact to the double. */
export function rawToWhole(raw: bigint, decimals: number): number {
  if (decimals <= 0) return Number(raw)
  const scale = 10n ** BigInt(decimals)
  return Number(raw / scale) + Number(raw % scale) / Number(scale)
}

/** Whole tokens from raw units as an exact decimal string ("1.5", "0"). */
export function rawToDecimal(raw: bigint, decimals: number): string {
  if (decimals <= 0) return raw.toString()
  const s = raw.toString().padStart(decimals + 1, '0')
  const int = s.slice(0, -decimals)
  const frac = s.slice(-decimals).replace(/0+$/, '')
  return frac === '' ? int : `${int}.${frac}`
}
